import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import HRLayout from '../../components/layout/HRLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Briefcase,
  Mail,
  Phone,
  Shield,
  Search,
  CheckCircle2,
  Archive as ArchiveIcon,
  RotateCcw,
  Copy,
  X,
  RefreshCw,
  Link2,
  UserCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TABS = [
  { id: 'pending', label: 'Pending', color: 'text-amber-400' },
  { id: 'invite_sent', label: 'Invite Sent', color: 'text-blue-400' },
  { id: 'linked', label: 'Linked', color: 'text-emerald-400' },
  { id: 'archived', label: 'Archived', color: 'text-zinc-400' },
  { id: 'all', label: 'All', color: 'text-foreground' },
];

const STATUS_BADGE = {
  pending: { label: 'PENDING', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/40' },
  approved: { label: 'APPROVED', cls: 'bg-green-500/15 text-green-400 border-green-500/40' },
  invite_sent: { label: 'INVITE SENT', cls: 'bg-blue-500/15 text-blue-400 border-blue-500/40' },
  linked: { label: 'LINKED', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40' },
  archived: { label: 'ARCHIVED', cls: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/40' },
};

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    return `${months}mo ago`;
  } catch {
    return '';
  }
}

export default function JobApplicationsPage({ hrMode = false }) {
  const Layout = hrMode ? HRLayout : AdminLayout;
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, invite_sent: 0, linked: 0, archived: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [search, setSearch] = useState('');

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);

  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiveBusy, setArchiveBusy] = useState(false);

  const [inviteLinkModal, setInviteLinkModal] = useState(null); // {link, name}
  const [actionBusy, setActionBusy] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [linking, setLinking] = useState(false);

  // Personalised onboarding requirements picked by admin before approval.
  // Empty by default — admin bifează manual tot per spec.
  const [reqDocs, setReqDocs] = useState([]);
  const [reqSteps, setReqSteps] = useState([]);

  const fetchList = useCallback(async () => {
    try {
      const params = activeTab === 'all' ? {} : { status: activeTab };
      const res = await axios.get(`${API}/admin/job-applications`, { params });
      setApplications(res.data.applications || []);
      setCounts(res.data.counts || {});
    } catch (e) {
      console.error('Failed to load job applications', e);
      toast.error('Failed to load job applications');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    setLoading(true);
    fetchList();
  }, [fetchList]);

  const fetchDetail = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API}/admin/job-applications/${id}`);
      setDetail(res.data);
      setNotesDraft(res.data.admin_notes || '');
    } catch (e) {
      console.error('Failed to load application detail', e);
      toast.error('Failed to load application');
    }
  }, []);

  useEffect(() => {
    if (selectedId) {
      setDetail(null);
      // Fresh application → clear the per-user requirement selections;
      // admin bifează manual tot pentru fiecare aplicație.
      setReqDocs([]);
      setReqSteps([]);
      fetchDetail(selectedId);
    }
  }, [selectedId, fetchDetail]);

  const saveNotes = async () => {
    if (!detail) return;
    setNotesSaving(true);
    try {
      await axios.put(`${API}/admin/job-applications/${detail.id}/notes`, { notes: notesDraft });
      toast.success('Notes saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save notes');
    } finally {
      setNotesSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!detail) return;
    if (reqDocs.length === 0 && reqSteps.length === 0) {
      toast.error('Please tick at least one requirement (document or step) before approving.');
      return;
    }
    setActionBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/job-applications/${detail.id}/approve`,
        {
          required_documents: reqDocs,
          required_steps: reqSteps,
        },
      );
      toast.success(res.data.message || 'Invitation sent');
      setInviteLinkModal({ link: res.data.invite_link, name: detail.full_name });
      await fetchList();
      await fetchDetail(detail.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    } finally {
      setActionBusy(false);
    }
  };

  const handleArchive = async () => {
    if (!detail) return;
    setArchiveBusy(true);
    try {
      await axios.post(`${API}/admin/job-applications/${detail.id}/archive`, { reason: archiveReason });
      toast.success('Application archived');
      setArchiveOpen(false);
      setArchiveReason('');
      await fetchList();
      // close panel since item moved out of active tab in most cases
      setSelectedId(null);
      setDetail(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to archive');
    } finally {
      setArchiveBusy(false);
    }
  };

  const handleRestore = async () => {
    if (!detail) return;
    setActionBusy(true);
    try {
      await axios.post(`${API}/admin/job-applications/${detail.id}/restore`);
      toast.success('Application restored');
      await fetchList();
      await fetchDetail(detail.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to restore');
    } finally {
      setActionBusy(false);
    }
  };

  const copyInviteLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Invite link copied');
    } catch {
      toast.error('Could not copy. Please copy manually.');
    }
  };

  const filtered = applications.filter((a) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (a.full_name || '').toLowerCase().includes(q) ||
      (a.email || '').toLowerCase().includes(q) ||
      (a.phone || '').toLowerCase().includes(q)
    );
  });

  return (
    <Layout>
      <div className="p-4 sm:p-6 space-y-6 min-h-screen" data-testid="job-applications-page">
        <div className="border-b border-border pb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-barlow tracking-widest flex items-center gap-3">
              <Briefcase className="w-6 h-6 text-primary" />
              JOB APPLICATIONS
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Door Supervisor applications submitted via the public application form.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 self-start sm:self-auto">
            {!hrMode && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="link-existing-btn"
              disabled={linking}
              onClick={async () => {
                setLinking(true);
                try {
                  const r = await axios.post(`${API}/admin/job-applications/link-existing`);
                  const { linked = 0, possible_matches_flagged = 0 } = r.data || {};
                  toast.success(`Linked ${linked} application${linked === 1 ? '' : 's'} to existing employees (${possible_matches_flagged} possible matches flagged)`);
                  await fetchList();
                  if (detail) await fetchDetail(detail.id);
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to link');
                } finally {
                  setLinking(false);
                }
              }}
              className="rounded-sm border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 uppercase tracking-wider text-[11px] gap-2"
            >
              <Link2 className={cn('w-3.5 h-3.5', linking && 'animate-pulse')} strokeWidth={2} />
              {linking ? 'Linking…' : 'Link existing employees'}
            </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="reprocess-applications-btn"
              disabled={reprocessing}
            onClick={async () => {
              setReprocessing(true);
              try {
                const r = await axios.post(`${API}/admin/job-applications/reprocess`);
                const { healed = 0, skipped = 0 } = r.data || {};
                toast.success(`Reprocessed ${healed} application${healed === 1 ? '' : 's'} (skipped ${skipped})`);
                await fetchList();
                if (detail) await fetchDetail(detail.id);
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Failed to reprocess');
              } finally {
                setReprocessing(false);
              }
            }}
            className="rounded-sm border-primary/50 text-primary hover:bg-primary/10 uppercase tracking-wider text-[11px] gap-2"
          >
            <RefreshCw className={cn('w-3.5 h-3.5', reprocessing && 'animate-spin')} strokeWidth={2} />
            {reprocessing ? 'Reprocessing…' : 'Rebuild fields'}
          </Button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="job-applications-kpis">
          {[
            { key: 'pending', label: 'Pending', color: 'text-amber-400' },
            { key: 'invite_sent', label: 'Invite Sent', color: 'text-blue-400' },
            { key: 'linked', label: 'Linked', color: 'text-emerald-400' },
            { key: 'archived', label: 'Archived', color: 'text-zinc-400' },
            { key: 'total', label: 'Total', color: 'text-primary' },
          ].map((k) => (
            <Card key={k.key} className="rounded-sm" data-testid={`kpi-${k.key}`}>
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{k.label}</p>
                <p className={cn('text-3xl font-barlow mt-2', k.color)}>{counts[k.key] || 0}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-2">
          {STATUS_TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setActiveTab(t.id)}
              className={cn(
                'px-3 py-1.5 rounded-sm text-xs uppercase tracking-wider border transition-colors',
                activeTab === t.id
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:text-foreground'
              )}
            >
              {t.label}
              {t.id !== 'all' && counts[t.id] > 0 && (
                <span className="ml-2 text-[10px] opacity-70">({counts[t.id]})</span>
              )}
            </button>
          ))}
          <div className="relative ml-auto w-full sm:w-auto sm:min-w-[260px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              data-testid="job-applications-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, phone…"
              className="pl-8 h-9 rounded-sm"
            />
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="py-12 text-center text-muted-foreground animate-pulse">Loading…</div>
        ) : filtered.length === 0 ? (
          <Card className="rounded-sm">
            <CardContent className="p-10 text-center text-muted-foreground">
              No applications in this view.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="job-applications-list">
            {filtered.map((a) => {
              const badge = STATUS_BADGE[a.status] || STATUS_BADGE.pending;
              return (
                <button
                  key={a.id}
                  data-testid={`application-card-${a.id}`}
                  onClick={() => setSelectedId(a.id)}
                  className="w-full text-left"
                >
                  <Card className="rounded-sm hover:border-primary/60 transition-colors">
                    <CardContent className="p-4 flex flex-wrap items-center gap-4">
                      <div className="flex-1 min-w-[200px]">
                        <p className="text-sm font-medium">{a.full_name}</p>
                        <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-muted-foreground">
                          {a.email && (
                            <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{a.email}</span>
                          )}
                          {a.phone && (
                            <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{a.phone}</span>
                          )}
                          {a.sia_license && (
                            <span className="flex items-center gap-1"><Shield className="w-3 h-3" />SIA {a.sia_license}</span>
                          )}
                          {a.position && (
                            <span
                              className="flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-primary/10 text-primary border border-primary/30 font-medium"
                              data-testid={`app-position-${a.id}`}
                            >
                              <Briefcase className="w-3 h-3" />{a.position}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[11px] text-muted-foreground">{timeAgo(a.submitted_at)}</span>
                        <Badge className={cn('text-[10px] uppercase tracking-wider border rounded-sm', badge.cls)}>
                          {badge.label}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Sheet */}
      <Sheet open={!!selectedId} onOpenChange={(open) => { if (!open) { setSelectedId(null); setDetail(null); } }}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="application-detail-sheet">
          <SheetHeader>
            <SheetTitle className="font-barlow tracking-widest uppercase">
              {detail?.full_name || 'Loading…'}
            </SheetTitle>
            <SheetDescription>
              Submitted {detail ? timeAgo(detail.submitted_at) : ''}
            </SheetDescription>
          </SheetHeader>

          {!detail ? (
            <div className="py-10 text-center text-muted-foreground animate-pulse">Loading…</div>
          ) : (
            <div className="space-y-6 mt-6">
              {/* Status + summary */}
              <div className="flex items-center justify-between">
                <Badge className={cn('text-[10px] uppercase tracking-wider border rounded-sm', (STATUS_BADGE[detail.status] || STATUS_BADGE.pending).cls)}>
                  {(STATUS_BADGE[detail.status] || STATUS_BADGE.pending).label}
                </Badge>
                {detail.invite_sent_at && (
                  <span className="text-xs text-muted-foreground">Invite sent {timeAgo(detail.invite_sent_at)}</span>
                )}
              </div>

              {/* Linked-to-existing-employee banner */}
              {detail.status === 'linked' && detail.linked_user_id && (
                <div className="rounded-sm border border-emerald-500/40 bg-emerald-500/10 p-3 space-y-2" data-testid="linked-banner">
                  <p className="text-sm text-emerald-400 flex items-center gap-2">
                    <UserCheck className="w-4 h-4 shrink-0" />
                    Linked to existing employee: <b>{detail.linked_user_name}</b>
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {Array.isArray(detail.linked_fields_filled) && detail.linked_fields_filled.length > 0
                      ? `${detail.linked_fields_filled.length} empty profile field${detail.linked_fields_filled.length === 1 ? '' : 's'} auto-filled from this application (${detail.linked_fields_filled.join(', ')}).`
                      : 'No profile fields needed filling — the profile was already complete.'}
                    {detail.linked_at ? ` Linked ${timeAgo(detail.linked_at)}${detail.linked_by ? ` by ${detail.linked_by}` : ''}.` : ''}
                  </p>
                  {!hrMode && (
                    <Button
                      size="sm"
                      onClick={() => navigate(`/admin/employees/${detail.linked_user_id}`)}
                      className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white uppercase tracking-wider text-[11px]"
                      data-testid="view-linked-profile-btn"
                    >
                      View profile
                    </Button>
                  )}
                </div>
              )}

              {/* Email-match banner: this email already belongs to a portal user */}
              {detail.status !== 'linked' && detail.status !== 'archived' && detail.email_match_user_id && (
                <div className="rounded-sm border border-emerald-500/40 bg-emerald-500/10 p-3 space-y-2" data-testid="email-match-banner">
                  <p className="text-sm text-emerald-400 flex items-center gap-2">
                    <UserCheck className="w-4 h-4 shrink-0" />
                    This email already belongs to existing employee: <b>{detail.email_match_user_name}</b>
                  </p>
                  {!hrMode && (
                  <Button
                    size="sm"
                    disabled={actionBusy}
                    onClick={async () => {
                      setActionBusy(true);
                      try {
                        const r = await axios.post(`${API}/admin/job-applications/${detail.id}/link`, { user_id: detail.email_match_user_id });
                        toast.success(`Linked to ${r.data.user_name} — ${r.data.fields_filled?.length || 0} profile fields filled`);
                        await fetchList();
                        await fetchDetail(detail.id);
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Link failed');
                      } finally {
                        setActionBusy(false);
                      }
                    }}
                    className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white uppercase tracking-wider text-[11px]"
                    data-testid="email-match-link-btn"
                  >
                    <Link2 className="w-3.5 h-3.5 mr-1.5" />
                    Link &amp; fill profile
                  </Button>
                  )}
                </div>
              )}

              {/* Possible-match suggestion (name + DOB) — admin confirms manually */}
              {detail.status === 'pending' && detail.possible_match_user_id && (
                <div className="rounded-sm border border-amber-500/40 bg-amber-500/10 p-3 space-y-2" data-testid="possible-match-banner">
                  <p className="text-sm text-amber-400 flex items-center gap-2">
                    <Link2 className="w-4 h-4 shrink-0" />
                    Possible match: <b>{detail.possible_match_user_name}</b> (same name + date of birth, different email)
                  </p>
                  {!hrMode && (
                  <Button
                    size="sm"
                    disabled={actionBusy}
                    onClick={async () => {
                      setActionBusy(true);
                      try {
                        const r = await axios.post(`${API}/admin/job-applications/${detail.id}/link`, { user_id: detail.possible_match_user_id });
                        toast.success(`Linked to ${r.data.user_name} — ${r.data.fields_filled?.length || 0} profile fields filled`);
                        await fetchList();
                        await fetchDetail(detail.id);
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Link failed');
                      } finally {
                        setActionBusy(false);
                      }
                    }}
                    className="rounded-sm bg-amber-600 hover:bg-amber-500 text-white uppercase tracking-wider text-[11px]"
                    data-testid="confirm-link-btn"
                  >
                    Link &amp; fill profile
                  </Button>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 gap-2 text-sm">
                <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-muted-foreground" />{detail.email || '—'}</div>
                <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-muted-foreground" />{detail.phone || '—'}</div>
                <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-muted-foreground" />SIA {detail.sia_license || '—'}</div>
                <div className="flex items-center gap-2"><Briefcase className="w-4 h-4 text-muted-foreground" />Position: <span className="font-medium text-foreground">{detail.position || '—'}</span></div>
              </div>

              {/* Comprehensive application details — only the rows that
                  have a value are rendered, so unrelated fields don't show
                  up as empty placeholders on shorter forms. */}
              <ApplicationFields detail={detail} />

              {/* Onboarding Requirements — admin ticks EXACTLY what this
                  specific employee must complete before the gate lifts.
                  Only rendered while the application is pending so we
                  don't offer "modify requirements" from here after the
                  invite is out (that's on Employee Detail). */}
              {detail.status === 'pending' && (
                <OnboardingRequirementsPanel
                  docs={reqDocs}
                  steps={reqSteps}
                  onDocsChange={setReqDocs}
                  onStepsChange={setReqSteps}
                />
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2 border-t border-b border-border py-4">
                {detail.status === 'pending' && (
                  <>
                    <Button
                      data-testid="approve-btn"
                      disabled={actionBusy}
                      onClick={handleApprove}
                      className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm uppercase tracking-wider text-xs"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Approve &amp; Send Invite
                    </Button>
                    <Button
                      data-testid="archive-btn"
                      variant="outline"
                      onClick={() => setArchiveOpen(true)}
                      className="rounded-sm uppercase tracking-wider text-xs"
                    >
                      <ArchiveIcon className="w-4 h-4 mr-2" />
                      Archive
                    </Button>
                  </>
                )}
                {detail.status === 'invite_sent' && (
                  <>
                    <Button
                      data-testid="copy-invite-link-btn"
                      onClick={() => copyInviteLink(`${process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '')}/register?token=${detail.invite_token}`)}
                      className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm uppercase tracking-wider text-xs"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Invite Link
                    </Button>
                    <Button
                      data-testid="archive-btn"
                      variant="outline"
                      onClick={() => setArchiveOpen(true)}
                      className="rounded-sm uppercase tracking-wider text-xs"
                    >
                      <ArchiveIcon className="w-4 h-4 mr-2" />
                      Archive
                    </Button>
                  </>
                )}
                {detail.status === 'archived' && (
                  <Button
                    data-testid="restore-btn"
                    disabled={actionBusy}
                    onClick={handleRestore}
                    variant="outline"
                    className="rounded-sm uppercase tracking-wider text-xs"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Restore to {detail.archived_from_status || 'pending'}
                  </Button>
                )}
              </div>

              {/* Internal Notes */}
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Internal Admin Notes</p>
                <Textarea
                  data-testid="admin-notes-textarea"
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  rows={4}
                  className="rounded-sm"
                  placeholder="Notes visible to admins only…"
                />
                <div className="mt-2 flex justify-end">
                  <Button
                    size="sm"
                    onClick={saveNotes}
                    disabled={notesSaving}
                    className="rounded-sm uppercase tracking-wider text-xs"
                  >
                    {notesSaving ? 'Saving…' : 'Save notes'}
                  </Button>
                </div>
              </div>

              {/* All Answers */}
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">All Answers</p>
                {(detail.flat_answers || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">No additional answers found in payload.</p>
                ) : (
                  <div className="space-y-2" data-testid="all-answers">
                    {detail.flat_answers.map((pair, idx) => (
                      <div key={idx} className="bg-card border border-border rounded-sm p-3">
                        <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{pair.question}</p>
                        <p className="text-sm mt-1 break-words whitespace-pre-wrap">{pair.answer}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Audit trail */}
              <div className="text-xs text-muted-foreground space-y-1 border-t border-border pt-4">
                {detail.decided_at && <p>Decision made {timeAgo(detail.decided_at)}</p>}
                {detail.archived_at && (
                  <p>
                    Archived {timeAgo(detail.archived_at)}
                    {detail.archive_reason ? ` — "${detail.archive_reason}"` : ''}
                  </p>
                )}
                {detail.restored_at && <p>Restored {timeAgo(detail.restored_at)}</p>}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Archive confirm modal */}
      <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <DialogContent data-testid="archive-modal">
          <DialogHeader>
            <DialogTitle>Archive application</DialogTitle>
            <DialogDescription>
              The application stays in the system for audit but moves to the Archived tab. You can restore it later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Reason (optional)</p>
            <Textarea
              data-testid="archive-reason-textarea"
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              rows={3}
              placeholder="e.g. Position filled, not enough experience…"
              className="rounded-sm"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setArchiveOpen(false)}
              disabled={archiveBusy}
              className="rounded-sm uppercase tracking-wider text-xs"
            >
              Cancel
            </Button>
            <Button
              data-testid="confirm-archive-btn"
              onClick={handleArchive}
              disabled={archiveBusy}
              className="rounded-sm uppercase tracking-wider text-xs"
            >
              {archiveBusy ? 'Archiving…' : 'Confirm Archive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite-sent confirmation modal with copyable link */}
      <Dialog open={!!inviteLinkModal} onOpenChange={(open) => {
        if (!open) {
          setInviteLinkModal(null);
          // Belt-and-braces: re-fetch the list once the modal closes, so the
          // card is guaranteed to appear in its new tab without a manual reload.
          fetchList();
        }
      }}>
        <DialogContent data-testid="invite-sent-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              Invitation sent
            </DialogTitle>
            <DialogDescription>
              An invitation email has been sent to {inviteLinkModal?.name}. The link expires in 7 days.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-card border border-border rounded-sm p-3 text-xs break-all">
            {inviteLinkModal?.link}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setInviteLinkModal(null)}
              className="rounded-sm uppercase tracking-wider text-xs"
            >
              <X className="w-4 h-4 mr-2" />
              Close
            </Button>
            <Button
              data-testid="copy-link-modal-btn"
              onClick={() => copyInviteLink(inviteLinkModal.link)}
              className="rounded-sm uppercase tracking-wider text-xs"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

// ----- Application detail sections (admin) -----
// Renders ONLY the canonical Forms.app application fields the user listed in
// the spec, grouped into Personal / SIA / Address / ID / Employment /
// Emergency. Empty values are skipped so unrelated rows never clutter the
// sheet. Existing `All Answers` flat dump still appears below when the form
// includes additional ad-hoc questions.
const APPLICATION_FIELD_GROUPS = [
  {
    title: 'Personal Information',
    fields: [
      ['first_name',     'First name'],
      ['middle_name',    'Middle name'],
      ['surname',        'Surname'],
      ['gender',         'Gender'],
      ['date_of_birth',  'Date of birth'],
    ],
  },
  {
    title: 'SIA',
    fields: [
      ['sia_license',            'SIA Badge number'],
      ['sia_badge_expiry_date',  'SIA Badge expiry date'],
    ],
  },
  {
    title: 'Contact & Address',
    fields: [
      ['phone',     'Contact number'],
      ['email',     'E-mail'],
      ['address',   'Address'],
      ['city',      'City'],
      ['postcode',  'Postcode'],
    ],
  },
  {
    title: 'Identity Document',
    fields: [
      ['id_document_type',     'ID Document type'],
      ['id_document_number',   'ID Document number'],
      ['id_expiry_date',       'ID expiry date'],
      ['id_issue_date',        'ID issue date'],
      ['id_country_of_issue',  'ID Country of issue'],
    ],
  },
  {
    title: 'Employment & Bank',
    fields: [
      ['current_employment_status', 'What is your current employment status?'],
      ['bank_details',              'Bank details'],
    ],
  },
  {
    title: 'Emergency Contact',
    fields: [
      ['emergency_contact_name',   'EMERGENCY CONTACT NAME'],
      ['emergency_contact_number', 'EMERGENCY CONTACT NUMBER'],
    ],
  },
];

function ApplicationFields({ detail }) {
  if (!detail) return null;
  const groupsWithData = APPLICATION_FIELD_GROUPS
    .map((g) => ({
      title: g.title,
      rows: g.fields
        .map(([key, label]) => [label, detail[key]])
        .filter(([, v]) => v !== undefined && v !== null && v !== ''),
    }))
    .filter((g) => g.rows.length > 0);

  if (groupsWithData.length === 0) return null;

  return (
    <div className="space-y-4 border-t border-border pt-4" data-testid="application-fields">
      {groupsWithData.map((g) => (
        <div key={g.title} className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{g.title}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
            {g.rows.map(([label, value]) => (
              <div key={label} className="flex flex-col">
                <span className="text-[11px] text-muted-foreground">{label}</span>
                <span className="text-foreground font-medium break-words">{value}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}



// ==================== Onboarding Requirements Panel ====================
// Admin ticks per-employee onboarding requirements BEFORE approval. Both
// lists start empty (per spec — "nothing pre-ticked, admin bifează
// manual tot"). The Approve button refuses to proceed if both lists are
// empty so admin can't accidentally send an invitation with zero gate.

const AVAILABLE_DOCUMENTS = [
  { key: 'sia_door_supervisor', label: 'SIA Door Supervisor Licence' },
  { key: 'cctv', label: 'CCTV Licence' },
  { key: 'proof_of_address', label: 'Proof of Address' },
  { key: 'first_aid', label: 'First Aid Certificate' },
  { key: 'act_awareness', label: 'ACT Awareness Certificate' },
  { key: 'scan_training', label: 'SCaN Training Certificate' },
];

const AVAILABLE_STEPS = [
  { key: 'profile', label: 'Complete My Profile' },
  { key: 'documents', label: 'Upload Documents' },
  { key: 'policies', label: 'Read Company Policies' },
  { key: 'procedures', label: 'Read Procedures' },
  { key: 'terms', label: 'Accept Terms & Conditions' },
];


function OnboardingRequirementsPanel({ docs, steps, onDocsChange, onStepsChange }) {
  const toggle = (list, key, setter) => {
    setter(list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);
  };
  return (
    <div className="border border-border rounded-sm p-4 space-y-4" data-testid="onboarding-requirements-panel">
      <div>
        <h3 className="text-sm uppercase tracking-wider text-primary font-semibold">Onboarding Requirements</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Tick which documents and steps this specific employee must complete before they can access the app.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Required Documents</h4>
          <div className="space-y-1.5">
            {AVAILABLE_DOCUMENTS.map((d) => (
              <label key={d.key} className="flex items-center gap-2 cursor-pointer text-sm hover:text-primary transition-colors">
                <input
                  type="checkbox"
                  checked={docs.includes(d.key)}
                  onChange={() => toggle(docs, d.key, onDocsChange)}
                  data-testid={`req-doc-${d.key}`}
                  className="w-4 h-4 accent-primary"
                />
                {d.label}
              </label>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Required Onboarding Steps</h4>
          <div className="space-y-1.5">
            {AVAILABLE_STEPS.map((s) => (
              <label key={s.key} className="flex items-center gap-2 cursor-pointer text-sm hover:text-primary transition-colors">
                <input
                  type="checkbox"
                  checked={steps.includes(s.key)}
                  onChange={() => toggle(steps, s.key, onStepsChange)}
                  data-testid={`req-step-${s.key}`}
                  className="w-4 h-4 accent-primary"
                />
                {s.label}
              </label>
            ))}
          </div>
        </div>
      </div>

      {docs.length === 0 && steps.length === 0 && (
        <p className="text-xs text-orange-500">
          Please tick at least one requirement before sending the invitation.
        </p>
      )}
    </div>
  );
}
